import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const DigitalLibraryContext = createContext();

export function useDigitalLibrary() {
  return useContext(DigitalLibraryContext);
}

export function DigitalLibraryProvider({ children }) {
  const [textbooks, setTextbooks] = useState([]);
  const [examPapers, setExamPapers] = useState([]);
  const [researchPapers, setResearchPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    subject: '',
    university: '',
    year: '',
    language: 'en'
  });

  // Fetch all resources
  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const [textbooksSnapshot, examPapersSnapshot, researchPapersSnapshot] = await Promise.all([
          getDocs(collection(db, 'textbooks')),
          getDocs(collection(db, 'examPapers')),
          getDocs(collection(db, 'researchPapers'))
        ]);

        setTextbooks(textbooksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setExamPapers(examPapersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setResearchPapers(researchPapersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  // Upload new resource
  const uploadResource = async (resourceType, data, file) => {
    try {
      setLoading(true);
      
      // Upload file to storage
      const storageRef = ref(storage, `${resourceType}/${file.name}`);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      // Add document to Firestore
      const docRef = await addDoc(collection(db, resourceType), {
        ...data,
        fileUrl,
        uploadDate: new Date(),
        downloads: 0,
        rating: 0
      });

      return docRef.id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update resource
  const updateResource = async (resourceType, id, data) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, resourceType, id), data);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete resource
  const deleteResource = async (resourceType, id) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, resourceType, id));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Filter resources
  const filterResources = (resourceType, filters) => {
    let resources = [];
    switch (resourceType) {
      case 'textbooks':
        resources = textbooks;
        break;
      case 'examPapers':
        resources = examPapers;
        break;
      case 'researchPapers':
        resources = researchPapers;
        break;
      default:
        return [];
    }

    return resources.filter(resource => {
      if (filters.subject && resource.subject !== filters.subject) return false;
      if (filters.university && resource.university !== filters.university) return false;
      if (filters.year && resource.year !== filters.year) return false;
      if (filters.language && resource.language !== filters.language) return false;
      return true;
    });
  };

  const value = {
    textbooks,
    examPapers,
    researchPapers,
    loading,
    error,
    filters,
    setFilters,
    uploadResource,
    updateResource,
    deleteResource,
    filterResources
  };

  return (
    <DigitalLibraryContext.Provider value={value}>
      {children}
    </DigitalLibraryContext.Provider>
  );
} 